import { AI_ASSISTANT_DEFAULT_SUGGESTIONS } from './ai-assistant.constants';
import { AI_ASSISTANT_KNOWLEDGE } from './ai-assistant.knowledge';
import { AiAssistantSuggestion, AiKnowledgeEntry } from './ai-assistant.types';
import { normalizeKnowledgeText } from './ai-assistant-knowledge.util';

/** Số thẻ liên quan sau mỗi câu trả lời. */
export const AI_ASSISTANT_MAX_RELATED = 3;

const KNOWLEDGE_BY_ID = new Map<string, AiKnowledgeEntry>(
    AI_ASSISTANT_KNOWLEDGE.map((entry) => [entry.id, entry]),
);

/**
 * Mục knowledge liên quan theo luồng nghiệp vụ / chủ đề kế tiếp.
 * Thứ tự trong mảng = ưu tiên hiển thị.
 */
const RELATED_ENTRY_IDS: Record<string, string[]> = {
    'auth-login': ['user-guide', 'permission-missing'],
    'user-guide': ['coord-truck-flow', 'oversee-layout', 'trip-chain'],
    'terminology': ['trip-chain', 'order-truck', 'order-tractor'],
    'dashboard-kpi': ['fleet-map', 'trip-chain'],
    'fleet-map': ['oversee-layout', 'coord-fleet-gps', 'dashboard-kpi'],
    'order-truck': ['coord-truck-flow', 'order-tractor', 'trip-list-truck'],
    'order-tractor': ['coord-tractor', 'terminology', 'order-truck'],
    'coord-truck-flow': ['coord-send-disabled', 'coord-plan', 'oversee-layout'],
    'coord-plan': ['coord-truck-flow', 'coord-route', 'coord-send-disabled'],
    'coord-route': ['coord-plan', 'coord-fleet-gps', 'coord-truck-flow'],
    'coord-fleet-gps': ['fleet-map', 'coord-truck-flow', 'oversee-layout'],
    'coord-split': ['coord-truck-flow', 'coord-vendor', 'coord-plan'],
    'coord-send-disabled': ['coord-truck-flow', 'coord-vendor', 'order-truck'],
    'coord-vendor': ['coord-truck-flow', 'coord-send-disabled'],
    'dispatched-trips-tab': ['trip-list-truck', 'coord-truck-flow', 'oversee-layout'],
    'coord-tractor': ['coord-send-disabled', 'order-tractor', 'oversee-tractor-extra'],
    'trip-list-truck': ['oversee-layout', 'coord-truck-flow', 'documents'],
    'trip-list-tractor': ['oversee-tractor-extra', 'coord-tractor', 'documents'],
    'oversee-layout': ['oversee-no-data', 'oversee-trip-status', 'oversee-point'],
    'oversee-no-data': ['oversee-layout', 'permission-missing', 'grid-common'],
    'oversee-trip-status': ['oversee-point', 'oversee-odo', 'documents'],
    'oversee-point': ['oversee-trip-status', 'oversee-odo', 'documents'],
    'oversee-odo': ['oversee-trip-status', 'oversee-point', 'oversee-camera'],
    'oversee-camera': ['oversee-layout', 'fleet-map'],
    'oversee-tractor-extra': ['oversee-point', 'oversee-layout', 'coord-tractor'],
    'mobile-v2': ['mobile-fuel', 'repair', 'oversee-trip-status'],
    'mobile-fuel': ['fuel-desktop', 'fuel-settle', 'mobile-v2'],
    'documents': ['trip-cost', 'oversee-trip-status', 'reconcile'],
    'trip-cost': ['reconcile', 'documents', 'bill'],
    'reconcile': ['bill', 'trip-cost', 'freight-rate'],
    'bill': ['reconcile', 'trip-chain'],
    'fuel-desktop': ['fuel-settle', 'fuel-norm', 'mobile-fuel'],
    'fuel-settle': ['fuel-norm', 'fuel-desktop'],
    'fuel-norm': ['fuel-settle', 'fuel-desktop'],
    'repair': ['vehicle-list', 'mobile-v2'],
    'advance': ['cost-type', 'trip-cost'],
    'freight-rate': ['contract', 'reconcile', 'partner'],
    'contract': ['freight-rate', 'reconcile', 'partner'],
    'vehicle-list': ['repair', 'employee', 'oversee-layout'],
    'employee': ['vehicle-list', 'salary-policy', 'coord-vendor'],
    'partner': ['order-truck', 'freight-rate', 'contract'],
    'setup-company': ['partner', 'vehicle-list', 'employee'],
    'cost-type': ['trip-cost', 'monthly-cost'],
    'salary-policy': ['salary-route', 'employee'],
    'salary-route': ['salary-policy', 'coord-tractor'],
    'bot-fee': ['monthly-cost', 'trip-cost'],
    'monthly-cost': ['cost-type', 'bot-fee'],
    'trip-chain': ['order-truck', 'coord-truck-flow', 'documents'],
    'trip-status-layers': ['trip-chain', 'oversee-trip-status'],
    'grid-common': ['permission-missing', 'user-guide'],
    'permission-missing': ['user-guide', 'grid-common'],
    'approval-flow': ['vehicle-list', 'contract', 'freight-rate'],
};

function entryToSuggestion(entry: AiKnowledgeEntry): AiAssistantSuggestion {
    const label = shortenLabel(entry.category || entry.questions[0] || entry.id);
    return {
        id: `rel-${entry.id}`,
        label,
        prompt: entry.questions[0],
    };
}

function shortenLabel(value: string, max = 28): string {
    const trimmed = value.trim();
    if (trimmed.length <= max) {
        return trimmed;
    }
    return `${trimmed.slice(0, max - 1)}…`;
}

function isSamePrompt(a: string, b: string): boolean {
    return normalizeKnowledgeText(a) === normalizeKnowledgeText(b);
}

/** Thẻ gợi ý cho tin chào mở đầu. */
export function getWelcomeRelatedSuggestions(): AiAssistantSuggestion[] {
    return AI_ASSISTANT_DEFAULT_SUGGESTIONS.slice(0, AI_ASSISTANT_MAX_RELATED).map((chip) => ({
        ...chip,
        id: `welcome-${chip.id}`,
    }));
}

/** Thẻ liên quan sau câu trả lời — bỏ câu user vừa hỏi nếu trùng. */
export function getRelatedSuggestions(
    entryId: string | null,
    excludePrompt?: string,
): AiAssistantSuggestion[] {
    if (!entryId) {
        return AI_ASSISTANT_DEFAULT_SUGGESTIONS
            .filter((chip) => !excludePrompt || !isSamePrompt(chip.prompt, excludePrompt))
            .slice(0, AI_ASSISTANT_MAX_RELATED)
            .map((chip) => ({ ...chip, id: `fallback-${chip.id}` }));
    }

    const chips: AiAssistantSuggestion[] = [];
    const seen = new Set<string>();
    const relatedIds = RELATED_ENTRY_IDS[entryId] ?? [];

    for (const id of relatedIds) {
        if (chips.length >= AI_ASSISTANT_MAX_RELATED) {
            break;
        }
        const entry = KNOWLEDGE_BY_ID.get(id);
        if (!entry) {
            continue;
        }
        const chip = entryToSuggestion(entry);
        if (excludePrompt && isSamePrompt(chip.prompt, excludePrompt)) {
            continue;
        }
        if (seen.has(chip.id)) {
            continue;
        }
        chips.push(chip);
        seen.add(chip.id);
    }

    if (chips.length < AI_ASSISTANT_MAX_RELATED) {
        const source = KNOWLEDGE_BY_ID.get(entryId);
        if (source) {
            for (const entry of AI_ASSISTANT_KNOWLEDGE) {
                if (entry.id === entryId) {
                    continue;
                }
                if (entry.category !== source.category) {
                    continue;
                }
                if (chips.length >= AI_ASSISTANT_MAX_RELATED) {
                    break;
                }
                const chip = entryToSuggestion(entry);
                if (excludePrompt && isSamePrompt(chip.prompt, excludePrompt)) {
                    continue;
                }
                if (seen.has(chip.id)) {
                    continue;
                }
                chips.push(chip);
                seen.add(chip.id);
            }
        }
    }

    return chips;
}
